import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

// reducers
import { fetchMediaGuides } from '../stores/mediaguides';

//components
import { BadgeActive } from '../components/commons/badge.status';

const PAGE_SIZE = 10;

// Derive a thumbnail image from a video url (YouTube supported; others → null)
const getThumbnail = (url = '') => {
  const yt = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg`;
  return null;
};


/**
 * TableMediaGuides
 * Lists settings media guides. Fetches once on mount (guarded by `initialized`),
 * paginates client-side (the API returns everything). No delete by design.
 *
 * Props:
 *   - onEdit: (mediaGuide) => void  — opens the edit modal pre-filled
 */
export const TableMediaGuides = ({ onEdit = () => {}, activeFilter = '' }) => {

  const dispatch = useDispatch();
  const { lists = [], loading = false, initialized = false } = useSelector(context => context.mediaGuides);
  const [page, setPage] = useState(1);

  // reset to page 1 whenever the filter changes
  useEffect(() => { setPage(1); }, [activeFilter]);

  useEffect(() => {
    if (!initialized) {
      const loadData = async () => {
        try {
          await dispatch(fetchMediaGuides()).unwrap();
        } catch (err) {
          console.error('tables.media.guides@loadData', err);
          toast.error('Failed to load media guides');
        }
      };
      loadData();
    }
  }, [initialized, dispatch]);

  const isActive = (v) => v === true || String(v).toLowerCase() === 'true';
  const filtered = lists.filter((m) => {
    if (activeFilter === 'active') return isActive(m.active);
    if (activeFilter === 'inactive') return !isActive(m.active);
    return true;
  });


  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);


  return (
    <div className="mt-8">
      <div className="mt-4 rounded-md shadow-md overflow-hidden">

        {loading && (
          <div className="p-8 flex items-center justify-center bg-white">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            <p className="ml-3 text-sm text-gray-500">Loading media guides...</p>
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div className="p-8 flex flex-col items-center justify-center bg-white">
            <p className="text-sm font-bold text-gray-500">No data</p>
            <p className="text-sm text-gray-500">No media guides found.</p>
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <table className="min-w-full">
            <thead className="border-y border-slate-200 bg-slate-100">
              <tr>
                <th className="py-4 pl-4 text-left text-sm font-semibold text-slate-800">Thumbnail</th>
                <th className="py-4 text-left text-sm font-semibold text-slate-800">Title</th>
                <th className="py-4 text-left text-sm font-semibold text-slate-800">Link</th>
                <th className="py-4 text-left text-sm font-semibold text-slate-800">Modified</th>
                <th className="py-4 text-left text-sm font-semibold text-slate-800">Active</th>
                <th className="py-4 pr-4 text-right text-sm font-semibold text-slate-800">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {pageItems.map((mediaGuide) => (
                <tr key={mediaGuide.resource_name} className="hover:bg-gray-50">
                  <td className="py-3 pl-4">
                    {(() => {
                      const thumb = getThumbnail(mediaGuide.url);
                      return thumb
                        ? <img src={thumb} alt="" className="h-10 w-16 object-cover rounded" />
                        : <div className="h-10 w-16 rounded bg-gray-100" />;
                    })()}
                  </td>
                  <td className="py-4">
                    <p className="text-sm font-semibold text-slate-800">{mediaGuide.title}</p>
                    <p className="text-xs text-gray-400 mt-1">{mediaGuide.subtitle}</p>
                  </td>
                  <td className="py-3">
                    <a href={mediaGuide.url} target="_blank" rel="noopener noreferrer"
                      className="text-sm text-primary-600 hover:underline break-all">
                      {mediaGuide.url}
                    </a>
                  </td>
                  <td className="py-3">
                    <p className="text-sm text-slate-600">{new Date(mediaGuide.modified).toLocaleDateString()}</p>
                  </td>
                  <td className="py-3">
                    <BadgeActive active={mediaGuide.active} />
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex justify-end gap-3">
                      <button onClick={() => onEdit(mediaGuide)} aria-label="Edit"
                        className="text-gray-400 hover:text-primary-600">
                        <PencilSquareIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* client-side pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-4 flex justify-end items-center gap-2 text-sm">
          <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
            className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-50">Prev</button>
          <span className="text-gray-500">Page {page} of {totalPages}</span>
          <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
            className="px-3 py-1 rounded-md border border-gray-300 disabled:opacity-50">Next</button>
        </div>
      )}
    </div>
  );
};
